import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { apiWrapper } from 'api';
import CodeBlock from 'components/CodeBlock';
import PageIntro from 'components/PageIntro';
import { palette } from 'styles/color';
import { Typography, Button } from 'design-component';
import { StlyedLink } from './Usage';
import MarkdownRenderer from 'utils/MarkdownRenderer';
import { IconArrowTopRight, IconCopy, IconSymbolChatGPT, IconSymbolClaude } from 'assets/icon';
import Dropdown from 'components/Dropdown';
import useGenerateToc from 'hooks/useGenerateToc';
import { Toc } from 'components/Toc';
import { useAppDispatch, useAppSelector } from 'store/store';
import { languageSelector } from 'store/slices/languageSlice';
import { activeToast } from 'store/slices/toastSlice';

const DATA_INSIGHT_REFERENCE_PAGE = 'https://datainsight.polarisoffice.com/api/reference';

type TDropdownOption = {
  id: 'chatgpt' | 'claude';
  labelKey: string;
  baseUrl: string;
  startIcon: React.ReactNode;
  endIcon: React.ReactNode;
};

const DROPDOWN_OPTIONS: TDropdownOption[] = [
  {
    id: 'chatgpt',
    labelKey: 'openInChatgpt',
    baseUrl: 'https://chatgpt.com/?hints=search&prompt=',
    startIcon: <IconSymbolChatGPT />,
    endIcon: <IconArrowTopRight />
  },
  {
    id: 'claude',
    labelKey: 'openInClaude',
    baseUrl: 'https://claude.ai/new?q=',
    startIcon: <IconSymbolClaude />,
    endIcon: <IconArrowTopRight />
  }
];

const ApiRef = () => {
  const { t } = useTranslation();
  const lang = useAppSelector(languageSelector);
  const markdownRef = useRef<HTMLDivElement>(null);

  const getApiRefContents = async () => {
    const res = await apiWrapper(`/api/v1/datainsight/playground/reference/doc-extract`, {
      method: 'GET'
    });

    const data = await res.json();

    return data.data;
  };

  const { data: apiRefContents, refetch: refetchApiRefContents } = useQuery({
    queryKey: ['api-ref-contents'],
    queryFn: getApiRefContents,
    select: (data) => {
      const { translation, request, response } = data;

      return {
        ref: {
          content: translation[lang].content || '',
          url: translation[lang].url || ''
        },
        request,
        response
      };
    },
    enabled: false
  });

  React.useEffect(() => {
    refetchApiRefContents();
  }, []);

  const tocItems = useGenerateToc(markdownRef, apiRefContents?.ref.content);

  return (
    <>
      <PageIntro
        title="API Reference"
        style={{
          width: '100%',
          maxWidth: '1920px',
          margin: '0 auto',
          marginBottom: '40px'
        }}>
        <Typography size="16px" weight={'normal'} color={palette.gray[500]}>
          <Trans i18nKey="apiReferenceDesc">
            여기서는 API 사용량을 확인할 수 있습니다. 더 자세한 정보
            <StlyedLink to="/documentation">Documentation.</StlyedLink> 메뉴를 참조하세요.
          </Trans>
        </Typography>

        <Typography
          size="16px"
          weight={'normal'}
          color={palette.gray[500]}
          style={{
            fontStyle: 'italic',
            marginTop: '-8px'
          }}>
          <Trans
            i18nKey="betaDesc"
            components={{
              1: (
                <StlyedLink
                  to={
                    lang === 'ko'
                      ? 'mailto:sales@polarisoffice.com '
                      : 'mailto:globalsales@polarisoffice.com'
                  }
                />
              )
            }}
          />
        </Typography>
      </PageIntro>

      {apiRefContents && (
        <Container>
          <div className="markdown-container" ref={markdownRef} style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: '40px', right: '5px' }}>
              <AskAIDropdown options={DROPDOWN_OPTIONS} mdUrl={apiRefContents?.ref.url} />
            </div>
            <MarkdownRenderer>{apiRefContents.ref.content || ''}</MarkdownRenderer>
          </div>
          {apiRefContents && (
            <>
              <div className="toc-container">
                <ApiPreview request={apiRefContents.request} response={apiRefContents.response} />
              </div>
              <div className="toc-container">
                <Toc items={tocItems} />
              </div>
            </>
          )}
        </Container>
      )}
    </>
  );
};

export default ApiRef;

const ApiPreview = (props: { request: any; response: any }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { request, response } = props;
  const [selectedRequestLang, setSelectedRequestLang] = useState('Python');
  const [selectedResponseType, setSelectedResponseType] = useState('200');

  const RES_OPTIONS = [
    {
      value: '200',
      label: 'HTTP 200 OK'
    },
    {
      value: '415',
      label: '415 Unsupported media type'
    },
    {
      value: '500',
      label: '500 Internal Server Error'
    },
    {
      value: '408',
      label: '408 Request Timeout'
    }
  ];

  const handleCopy = async ({
    copyText,
    successMsg = `${t('toast.copiedToClipboard')}`,
    errorMsg = `${t('toast.copyFailed')}`
  }: {
    copyText: string;
    successMsg?: string;
    errorMsg?: string;
  }) => {
    try {
      await navigator.clipboard.writeText(copyText);
      dispatch(
        activeToast({
          type: 'info',
          msg: `${successMsg}`
        })
      );
    } catch (err) {
      dispatch(
        activeToast({
          type: 'error',
          msg: `${errorMsg}`
        })
      );
    }
  };

  return (
    <PreviewContainer>
      <Section>
        <RequestUrl>
          <div>
            <Typography
              component="span"
              size="15px"
              weight={600}
              height="100%"
              color={palette.gray[700]}
              style={{ marginRight: '8px' }}>
              POST
            </Typography>
            <Typography component="span" size="15px" weight={400} height="100%">
              /doc-extract
            </Typography>
          </div>
          <div className="btns">
            <Button
              color="primary"
              onClick={() =>
                handleCopy({
                  copyText:
                    'https://datainsight-api.polarisoffice.com/api/v1/datainsight/doc-extract'
                })
              }
              startIcon={<IconCopy />}>
              {t('copy')}
            </Button>
          </div>
        </RequestUrl>
      </Section>

      <CodeSection>
        <div className="example-header">
          <Typography size="14px" weight={600} height="150%" color={palette.gray[700]}>
            EXAMPLE REQUEST
          </Typography>
        </div>
        <div
          className="
          example-lang-select">
          <Dropdown
            options={Object.keys(request).map((reqLang) => ({
              value: reqLang,
              label: reqLang
            }))}
            value={selectedRequestLang}
            onChange={(value) => setSelectedRequestLang(value)}
          />
          <Button
            color="primary"
            onClick={() => handleCopy({ copyText: request[selectedRequestLang] })}
            startIcon={<IconCopy />}>
            {t('copy')}
          </Button>
        </div>
        <div className="example-code">
          <CodeBlock
            language={selectedRequestLang === 'Python' ? 'python' : 'bash'}
            code={request[selectedRequestLang] || ''}
          />
        </div>
      </CodeSection>

      <CodeSection>
        <div className="example-header">
          <Typography size="14px" weight={600} height="150%" color={palette.gray[700]}>
            EXAMPLE RESPONSES
          </Typography>
        </div>
        <div
          className="
          example-lang-select">
          <Dropdown
            options={RES_OPTIONS}
            value={selectedResponseType}
            onChange={(value) => setSelectedResponseType(value)}
          />
          <Button
            color="primary"
            onClick={() =>
              handleCopy({
                copyText: JSON.stringify(response[selectedResponseType], null, 2) || ''
              })
            }
            startIcon={<IconCopy />}>
            {t('copy')}
          </Button>
        </div>
        <div className="example-code">
          <CodeBlock
            language="json"
            code={JSON.stringify(response[selectedResponseType], null, 2) || ''}
          />
        </div>
      </CodeSection>
    </PreviewContainer>
  );
};

const PreviewContainer = styled.div`
  margin-bottom: 40px;
  width: 520px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RequestUrl = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 14px;
  justify-content: space-between;
  background: ${palette.gray[100]};
  border-radius: 12px;
`;

const CodeSection = styled.div`
  border-radius: 12px;
  overflow: hidden;

  .example-header {
    background: ${palette.gray[100]};
    padding: 13.5px 14px;
    border-bottom: 1px solid ${palette.gray[200]};
  }
  .example-lang-select {
    background: ${palette.gray[100]};
    padding: 5px 14px;
    box-sizing: border-box;
    border-bottom: 1px solid ${palette.gray[200]};

    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .example-code {
  }
`;

export const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  width: 100%;
  max-width: 1960px;
  margin: 0 auto 80px;

  .markdown-container {
    width: 769px;
    padding: 0px 20px;
    line-height: 1.6;
    font-size: 16px;
    margin-top: -40px;

    overflow-y: auto;
  }

  .toc-container {
    width: fit-content;
    position: sticky;
    top: 0px;

    display: flex;
    gap: 30px;
  }
`;

// ASK AI Dropdown
const AskAIDropdown = ({ options, mdUrl }: { options: TDropdownOption[]; mdUrl: string }) => {
  const lang = useAppSelector(languageSelector);
  const { t } = useTranslation();

  const generateQueryMessage = (_url: string, lang: string): string => {
    return lang === 'ko'
      ? `${_url} 내용을 읽고, 내가 관련 질문을 하면 대답할 수 있도록 준비해줘.`
      : `Please take a look at ${_url} so I can ask related questions.`;
  };

  const handleOptionClick = (baseUrl: string, query: string) => {
    const fullUrl = `${baseUrl}${query}`;
    // console.log(`Opening URL: ${fullUrl}`);
    window.open(fullUrl, '_blank');
  };

  const dropDownOptions = options.map((opt) => ({
    value: opt.id,
    label: (
      <OptionContainer>
        <IconWrapper>{opt.startIcon}</IconWrapper>
        <Label>{t(opt.labelKey)}</Label>
        <IconWrapper>{opt.endIcon}</IconWrapper>
      </OptionContainer>
    ),
    onClick: () =>
      handleOptionClick(
        opt.baseUrl,
        generateQueryMessage(opt.id === 'chatgpt' ? DATA_INSIGHT_REFERENCE_PAGE : mdUrl, lang)
      )
  }));

  return (
    <Dropdown
      options={dropDownOptions}
      value={null}
      placeholder={t('askAiAboutThis')}
      align="right"
      headerStyle={{
        width: '195px',
        border: '1px solid #E4E4E7'
      }}
      listStyle={{ width: '195px' }}
      onChange={(selectedOption) => {
        // 선택된 옵션의 onClick 실행
        const option = dropDownOptions.find((opt) => opt.value === selectedOption);
        option?.onClick?.();
      }}
    />
  );
};

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const IconWrapper = styled.div`
  svg {
    display: block;
  }
`;

const Label = styled.div`
  flex-grow: 1;
  font-size: 14px;
  color: #333;
  text-align: left;
`;
